import LeadScore from '@/features/leads/LeadScore'
import { underlag } from '@/features/leads/score'
import { memo } from 'react'
import { Ico } from '@/ui/Ico'
import { countryOf, formatPhone } from '@/shared/lib/contact'
import { fmtDate } from '@/shared/lib/format'
import { cls } from '@/ui/cls'
import Avatar from '@/shared/Avatar'
import StarRating from '@/features/leads/StarRating'
import SalesforceMark from '@/features/leads/SalesforceMark'
import { SourceBadge } from '@/features/leads/SourceMark'
import { DoNotContactBadge } from '@/features/leads/workflow/DoNotContact'
import { Badge } from '@/components/ui/badge'
import SellerPicker from '@/features/leads/SellerPicker'
import { STAGES, type Stage } from '@/shared/domain'
import { stageLabel } from '@/shared/pipelineColors'
import { regionOfLocation, cityOf, locationLabel, UNKNOWN_CITY } from '@/shared/regions'
import type { LeadRow } from '@/shared/types'
import { EMAIL_STATUS_CLASS, PHONE_CELL_CLASS, STAGE_SELECT_CLASS, emailTitle } from './columns'
import { useBrand } from '@/shared/white-label/useBrand'
import { useLeadWrites } from '@/features/leads/workflow/useLeadMutations'
import { notifyFieldSaved } from '@/features/leads/workflow/writeFeedback'
import { READ_ONLY_TITLE, useCanEdit, writeControlProps } from '@/shared/write-access'

interface Props {
  lead: LeadRow
  index: number
  myEmail?: string
  // Absent when the user lacks `log_calls` — the permission the claim/release
  // routes enforce. No permission, no affordance.
  onClaim?: (id: string) => void
  onRelease?: (id: string) => void
  onOverlay: (id: string) => void
  onStartRating: (lead: LeadRow, rating: number) => void
  onStartLogCall: (lead: LeadRow, index: number) => void
}

// Memoized: the table renders every lead at once, so without this any parent
// re-render — opening a modal, editing one row — re-rendered all of them along
// with their ScoreRing, StarRating and Avatar children. Every callback prop is
// memoized at its source for this to hold.
function TableRow({
  lead: l, index, myEmail, onClaim, onRelease, onOverlay,
  onStartRating, onStartLogCall,
}: Props) {
  const { updateLead, writeError } = useLeadWrites()
  const canEdit = useCanEdit()
  const { person, anonymised } = useBrand()
  const mine = !!myEmail && !!l.assignedTo && l.assignedTo.toLowerCase() === myEmail.toLowerCase()
  // The server refuses calls and claims on a lead marked Kontakta ej, so the
  // row offers neither.
  const marked = !!l.doNotContact
  const phone = formatPhone(l.phone, countryOf(l.location))
  return (
    <tr className={cls(l.dueToday && 'row-due')}>
      <td className="cell-num">{index + 1}</td>
      <td className="cell-score cursor-pointer" onClick={() => onOverlay(l.id)} title="Öppna leadöversikt">
        <LeadScore lead={l} />
      </td>
      <td className="num text-ink-500">{underlag(l) ?? ''}</td>
      <td className="cell-sticky cursor-pointer" onClick={() => onOverlay(l.id)} title="Öppna leadöversikt">
        <div className="flex items-center gap-2 min-w-0">
          {l.dueToday && (
            /* The yellow fill measures 2.07:1 against the pale-yellow due row, so
               the dot needs an outline to have a shape at all. The previous
               [text-shadow:0_0_0_…] had no offset and no blur, so it painted
               nothing — a stroke defines the edge on any row colour. */
            <span
              className="text-brand text-xs shrink-0"
              style={{ WebkitTextStroke: '0.6px var(--color-ink-1000)' }}
              title="Förfaller idag"
            >●</span>
          )}
          <div className="min-w-0 flex-1">
            <div className="cell-company-name">
              <span title={l.company}>{l.company}</span>
              {l.sfLink && (
                <a
                  className="sf-inline"
                  href={l.sfLink}
                  target="_blank"
                  rel="noreferrer"
                  onClick={e => e.stopPropagation()}
                  title={`I Salesforce${l.sfStatus ? ` · ${l.sfStatus}` : ''}`}
                >
                  <SalesforceMark size={12} />
                </a>
              )}
              <SourceBadge source={l.source} />
              {l.doNotContact && (
                <DoNotContactBadge
                  dnc={l.doNotContact}
                  name={e => (anonymised ? person(e) : e.split('@')[0])}
                />
              )}
              {l.testLead && (
                <Badge
                  variant="outline"
                  icon={null}
                  title="Testlead för QA. Visas bara i adminens testkö."
                  className="h-[15px] border-ink-200 bg-ink-50 px-1 text-[9px] font-bold tracking-wide text-ink-700"
                >
                  Testlead
                </Badge>
              )}
            </div>
            <div className="cell-company-loc">{locationLabel(l.location)}</div>
            {writeError && <span role="alert" className="block text-xs text-[var(--color-danger)]">{writeError}</span>}
          </div>
        </div>
      </td>
      <td className="small">
        {regionOfLocation(l.location)}
      </td>
      <td className="small">
        {cityOf(l.location) || <span className="text-ink-500">{UNKNOWN_CITY}</span>}
      </td>
      <td className="cursor-pointer" onClick={() => onOverlay(l.id)} title="Öppna leadöversikt">
        <div className="cell-contact">
          <Avatar name={l.contact} size={22} tone="muted" />
          <div>
            <div className="cell-contact-name">{l.contact}</div>
            <div className="cell-contact-role">{l.contactRole}</div>
          </div>
        </div>
      </td>
      <td className="font-mono text-xs text-ink-700 small">
        {phone
          ? <div className={PHONE_CELL_CLASS} title={phone}>{phone}</div>
          : <span className="text-ink-500">—</span>}
      </td>
      <td className="font-mono text-[11px] text-ink-700">
        <div className="flex items-center gap-1 max-w-[220px] min-w-0">
          <span
            className={cls('overflow-hidden text-ellipsis whitespace-nowrap min-w-0 flex-1', EMAIL_STATUS_CLASS[l.emailStatus])}
            title={emailTitle(l.email, l.emailStatus)}
          >{l.email}</span>
        </div>
      </td>
      {l.attempts.map((a, i) => (
        <td
          key={i}
          className={cls('att-cell', !a && !marked && canEdit && 'att-cell-clickable')}
          onClick={e => {
            e.stopPropagation()
            if (!a && !marked && canEdit) onStartLogCall(l, i)
          }}
          title={a ? fmtDate(a, { time: true }) : marked ? 'Kontakta ej' : canEdit ? `Logga samtalsförsök ${i + 1}` : READ_ONLY_TITLE}
        >
          {a ? (
            <span className="att-tick">{Ico.check}</span>
          ) : marked ? null : (
            <span className="att-blank">+</span>
          )}
        </td>
      ))}
      <td className="center" onClick={e => e.stopPropagation()}>
        <button
          className={cls('jn jn-toggle', l.gotContact ? 'ja' : 'nej')}
          onClick={() => updateLead(l.id, { gotContact: !l.gotContact })}
          {...writeControlProps(canEdit, "Växla 'fick kontakt'")}
        >
          {l.gotContact ? 'JA' : 'NEJ'}
        </button>
      </td>
      <td className="cell-rating" onClick={e => e.stopPropagation()}>
        <StarRating
          value={l.feedback?.rating || 0}
          onChange={r => onStartRating(l, r)}
          size={14}
          noHint
          readOnly={!canEdit}
          title={canEdit ? undefined : READ_ONLY_TITLE}
        />
      </td>
      <td onClick={e => e.stopPropagation()}>
        <select
          className={cls('inline-select', STAGE_SELECT_CLASS[l.stage])}
          value={l.stage}
          {...writeControlProps(canEdit)}
          onChange={e => {
            const next = e.target.value as Stage
            const undo = updateLead(l.id, { stage: next },
              next === 'Booked' && l.stage !== 'Booked' && !l.bookedMeeting
                ? { kind: 'meeting', body: 'Bokad via stadieändring' } : undefined)
            notifyFieldSaved(`Stadie ändrat till ${stageLabel(next)} för ${l.company}.`, undo)
          }}
        >
          {STAGES.map(s => <option key={s} value={s}>{stageLabel(s)}</option>)}
        </select>
      </td>
      <td className="center" onClick={e => e.stopPropagation()}>
        <button
          className={cls('jn jn-toggle', l.bookedMeeting ? 'ja' : 'nej')}
          onClick={e => {
            if (e.detail > 1) return
            const next = !l.bookedMeeting
            const undo = updateLead(l.id, { bookedMeeting: next },
              next ? { kind: 'meeting', body: 'Markerad som bokad' } : undefined)
            notifyFieldSaved(
              next ? `Markerad som bokad för ${l.company}.` : `Bokat möte ångrat för ${l.company}.`,
              undo,
            )
          }}
          {...writeControlProps(canEdit, 'Möte bokat. Avmarkera för att dra tillbaka det från statistiken')}
        >
          {l.bookedMeeting ? 'JA' : 'NEJ'}
        </button>
      </td>
      <td className="center" onClick={e => e.stopPropagation()}>
        <button
          className={cls('jn jn-toggle', l.likelyBooking ? 'ja' : 'nej')}
          onClick={() => updateLead(l.id, { likelyBooking: !l.likelyBooking })}
          {...writeControlProps(canEdit, 'Sannolikt bok')}
        >
          {l.likelyBooking ? 'JA' : 'NEJ'}
        </button>
      </td>
      <td className="small">{fmtDate(l.leadDate)}</td>
      <td className="small whitespace-nowrap">
        {l.moveDate ? fmtDate(l.moveDate) : <span className="text-ink-500">—</span>}
        {l.officeHotel && (
          <span className="ml-1 text-[10px] text-ink-500" title="Kontorshotell">KH</span>
        )}
      </td>
      <td className="small whitespace-nowrap">
        {l.meetingAt
          ? <span className={cls(l.bookedMeeting && new Date(l.meetingAt) < new Date() ? 'text-ink-1000 font-medium' : '')} title={l.bookedMeeting && new Date(l.meetingAt) < new Date() ? 'Mötet har passerat — följ upp' : undefined}>{fmtDate(l.meetingAt)}</span>
          : <span className="text-ink-500">—</span>}
      </td>
      <td className="small whitespace-nowrap" onClick={e => e.stopPropagation()}>
        {l.allocationPending ? (
          <span className="text-xs text-ink-500" title="Reserverad för 40/40/20-fördelningen. En admin kan se mottagarnas status under Lead-fördelning.">
            Väntar på fördelning
          </span>
        ) : l.assignedTo ? (
          <span
            className={cls('text-[11px]', mine ? 'text-ink-1000 font-medium' : 'text-ink-700')}
            title={mine ? 'Tilldelad dig' : `Tilldelad ${person(l.assignedTo)}`}
          >
            {mine ? 'Du' : anonymised ? person(l.assignedTo) : l.assignedTo.split('@')[0]}
            {mine && onRelease && (
              <button
                className="ml-1.5 text-ink-500 hover:text-ink-1000 text-[10px] underline disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:text-ink-500"
                onClick={() => onRelease(l.id)}
                {...writeControlProps(canEdit, 'Lämna tillbaka leaden till poolen')}
              >
                släpp
              </button>
            )}
          </span>
        ) : onClaim && !marked ? (
          <button
            className="text-[11px] text-ink-500 hover:text-ink-1000 underline disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:text-ink-500"
            onClick={() => onClaim(l.id)}
            {...writeControlProps(canEdit, 'Ta den här leaden')}
          >
            ta
          </button>
        ) : (
          <span className="text-ink-500">—</span>
        )}
      </td>
      <td onClick={e => e.stopPropagation()}>
        <SellerPicker
          value={l.seller}
          className="text-[11px]"
          {...writeControlProps(canEdit)}
          onChange={v => { if (v !== (l.seller || null)) updateLead(l.id, { seller: v }) }}
        />
      </td>
      <td className="small">
        {l.sfStatus?.trim() ? (
          <span
            className="text-ink-700"
            title={`Salesforce-status${l.sfLeadOwner ? ` · ägare ${person(l.sfLeadOwner)}` : ''}`}
          >
            {l.sfStatus}
          </span>
        ) : (
          <span className="text-ink-500" title="Inget Salesforce-lead bakom det här företaget">—</span>
        )}
      </td>
      <td className="font-mono text-xs text-ink-700">{l.orgNo}</td>
    </tr>
  )
}

export default memo(TableRow)
